import type { BlockNode } from '../types';
import { ensureSiteChromeNodes } from '../utils/siteChrome';

export type PageArchetype =
  | 'landing'
  | 'courses'
  | 'profile'
  | 'results'
  | 'admissions'
  | 'store'
  | 'legal';

export interface BundlePageTemplate {
  id: string;
  bundleId: string;
  bundleName: string;
  archetype: PageArchetype;
  archetypeLabel: string;
  title: string;
  defaultPath: string;
  description: string;
  accentColor: string;
  generateRoot: (pageId: string, siteName?: string) => BlockNode;
}

export interface WebsiteBundle {
  id: string;
  fancyName: string;
  tagline: string;
  targetAudience: string;
  palette: {
    primary: string;
    secondary: string;
    background: string;
    surface: string;
    text: string;
  };
  navbarVariant: 'classic' | 'centered' | 'glass' | 'split' | 'dark' | 'minimal';
  footerVariant: 'multicolumn' | 'centered' | 'newsletter' | 'dark' | 'compact';
  pages: BundlePageTemplate[];
}

/**
 * Helper to build a clean BlockNode container
 */
function makeContainer(id: string, children: BlockNode[]): BlockNode {
  return {
    id: `root-${id}`,
    type: 'Container',
    category: 'layout',
    props: {},
    styles: { base: { padding: '0px' } },
    children,
  };
}

// -------------------------------------------------------------
// Helper to construct interchangeable bundle page blocks
// -------------------------------------------------------------
function createStandardLandingPage(
  bundleId: string,
  bundleName: string,
  accentColor: string,
  headline: string,
  subheadline: string,
  courseTitle: string
): BundlePageTemplate {
  return {
    id: `${bundleId}-landing`,
    bundleId,
    bundleName,
    archetype: 'landing',
    archetypeLabel: 'Landing Page',
    title: 'Home',
    defaultPath: '/',
    description: `High-converting entrance homepage designed with ${bundleName} branding.`,
    accentColor,
    generateRoot: (pageId, siteName = bundleName) =>
      ensureSiteChromeNodes(
        makeContainer(pageId, [
          {
            id: `hero-${bundleId}`,
            type: 'HeroSplit',
            category: 'layout',
            props: {
              headline,
              subheadline,
              ctaText: 'Explore Courses & Batches',
              ctaLink: '/courses',
            },
            styles: { base: { padding: '64px 24px', backgroundColor: '#f8fafc' } },
          },
          {
            id: `stats-${bundleId}`,
            type: 'StatsCounter',
            category: 'conversion',
            props: {
              stat1Value: '12,500+',
              stat1Label: 'Selections in JEE & NEET',
              stat2Value: '99.8%',
              stat2Label: 'Top Percentile Record',
              stat3Value: '48+',
              stat3Label: 'IITian & Doctor Faculty',
            },
            styles: { base: { padding: '40px 24px', backgroundColor: '#ffffff' } },
          },
          {
            id: `courses-${bundleId}`,
            type: 'CoachCourseCatalogBlock',
            category: 'erp_binding',
            props: {
              title: courseTitle,
              text: courseTitle,
            },
            styles: { base: { padding: '56px 24px', backgroundColor: '#f8fafc' } },
          },
          {
            id: `toppers-${bundleId}`,
            type: 'ToppersShowcase',
            category: 'academics',
            props: {
              headline: 'All-India Rankers Hall of Fame',
              exam: 'JEE Advanced & NEET National Tops',
            },
            styles: { base: { padding: '56px 24px', backgroundColor: '#ffffff' } },
          },
          {
            id: `cta-${bundleId}`,
            type: 'CallToActionBanner',
            category: 'conversion',
            props: {
              headline: 'Start Your Preparation with Free Diagnostic Test',
              subheadline: 'Book a 1-on-1 counseling session with our academic directors today.',
              buttonText: 'Register for Admission Test',
            },
            styles: { base: { padding: '48px 24px', backgroundColor: '#0f172a', color: '#ffffff' } },
          },
        ]),
        siteName
      ),
  };
}

function createStandardCoursesPage(
  bundleId: string,
  bundleName: string,
  accentColor: string,
  title: string
): BundlePageTemplate {
  return {
    id: `${bundleId}-courses`,
    bundleId,
    bundleName,
    archetype: 'courses',
    archetypeLabel: 'Courses & Batches Page',
    title: 'Courses',
    defaultPath: '/courses',
    description: `Comprehensive academic course directory and batch timetables.`,
    accentColor,
    generateRoot: (pageId, siteName = bundleName) =>
      ensureSiteChromeNodes(
        makeContainer(pageId, [
          {
            id: `c-header-${bundleId}`,
            type: 'Section',
            category: 'layout',
            props: {
              title: `${title} - Year-Long & Fast-Track Programs`,
              description: 'Classroom, Live Online, and Hybrid batches tailored for competitive entrance excellence.',
            },
            styles: { base: { padding: '48px 24px', backgroundColor: '#f1f5f9' } },
          },
          {
            id: `c-catalog-${bundleId}`,
            type: 'CoachCourseCatalogBlock',
            category: 'erp_binding',
            props: {
              title: 'Target Batches & Comprehensive Curriculum',
              text: 'Target Batches & Comprehensive Curriculum',
            },
            styles: { base: { padding: '56px 24px', backgroundColor: '#ffffff' } },
          },
          {
            id: `c-pricing-${bundleId}`,
            type: 'PricingTable',
            category: 'commerce',
            props: {
              headline: 'Transparent Fee Structure & Scholarship Tiers',
            },
            styles: { base: { padding: '56px 24px', backgroundColor: '#f8fafc' } },
          },
          {
            id: `c-faq-${bundleId}`,
            type: 'AccordionFAQ',
            category: 'academics',
            props: {
              headline: 'Frequently Asked Questions about Batches & Tests',
            },
            styles: { base: { padding: '48px 24px', backgroundColor: '#ffffff' } },
          },
        ]),
        siteName
      ),
  };
}

function createStandardFacultyPage(
  bundleId: string,
  bundleName: string,
  accentColor: string
): BundlePageTemplate {
  return {
    id: `${bundleId}-faculty`,
    bundleId,
    bundleName,
    archetype: 'profile',
    archetypeLabel: 'Faculty & Mentors Profile',
    title: 'Faculty Profiles',
    defaultPath: '/faculty',
    description: `Distinguished educators, author-mentors, and faculty credentials showcase.`,
    accentColor,
    generateRoot: (pageId, siteName = bundleName) =>
      ensureSiteChromeNodes(
        makeContainer(pageId, [
          {
            id: `f-hero-${bundleId}`,
            type: 'Section',
            category: 'layout',
            props: {
              title: 'Learn from India\'s Top Educators & IITians',
              description: 'Our faculty members are textbook authors, national Olympiad coaches, and master problem solvers.',
            },
            styles: { base: { padding: '48px 24px', backgroundColor: '#f8fafc' } },
          },
          {
            id: `f-showcase-${bundleId}`,
            type: 'FacultyShowcase',
            category: 'academics',
            props: {
              headline: 'Head of Departments & Core Teaching Mentors',
            },
            styles: { base: { padding: '56px 24px', backgroundColor: '#ffffff' } },
          },
          {
            id: `f-reviews-${bundleId}`,
            type: 'Testimonials',
            category: 'academics',
            props: {
              headline: 'What Students Say About Our Mentorship',
            },
            styles: { base: { padding: '56px 24px', backgroundColor: '#f1f5f9' } },
          },
        ]),
        siteName
      ),
  };
}

function createStandardResultsPage(
  bundleId: string,
  bundleName: string,
  accentColor: string
): BundlePageTemplate {
  return {
    id: `${bundleId}-results`,
    bundleId,
    bundleName,
    archetype: 'results',
    archetypeLabel: 'Results & Toppers Page',
    title: 'Results & Ranks',
    defaultPath: '/results',
    description: `Hall of fame, AIR scorecards, and verifiable topper testimonials.`,
    accentColor,
    generateRoot: (pageId, siteName = bundleName) =>
      ensureSiteChromeNodes(
        makeContainer(pageId, [
          {
            id: `r-head-${bundleId}`,
            type: 'Section',
            category: 'layout',
            props: {
              title: 'Unrivaled Results in IIT-JEE, NEET & Olympiads',
              description: 'Authentic All-India Ranks produced through disciplined test analysis and continuous mentorship.',
            },
            styles: { base: { padding: '48px 24px', backgroundColor: '#0f172a', color: '#ffffff' } },
          },
          {
            id: `r-toppers-${bundleId}`,
            type: 'ToppersShowcase',
            category: 'academics',
            props: {
              headline: 'Top 100 All-India Rankers Gallery',
            },
            styles: { base: { padding: '56px 24px', backgroundColor: '#ffffff' } },
          },
          {
            id: `r-notices-${bundleId}`,
            type: 'SchoolNoticeBoardBlock',
            category: 'erp_binding',
            props: {
              title: 'Official Selection Lists & Verification Circulars',
            },
            styles: { base: { padding: '48px 24px', backgroundColor: '#f8fafc' } },
          },
        ]),
        siteName
      ),
  };
}

function createStandardAdmissionsPage(
  bundleId: string,
  bundleName: string,
  accentColor: string
): BundlePageTemplate {
  return {
    id: `${bundleId}-admissions`,
    bundleId,
    bundleName,
    archetype: 'admissions',
    archetypeLabel: 'Admissions & Desk',
    title: 'Admissions & Scholarships',
    defaultPath: '/admissions',
    description: `Registration forms, scholarship test booking, and campus helpdesk.`,
    accentColor,
    generateRoot: (pageId, siteName = bundleName) =>
      ensureSiteChromeNodes(
        makeContainer(pageId, [
          {
            id: `adm-head-${bundleId}`,
            type: 'Section',
            category: 'layout',
            props: {
              title: 'Admissions Open for 2026–2027 Academic Sessions',
              description: 'Secure up to 90% scholarship through the National Talent Recognition Test.',
            },
            styles: { base: { padding: '48px 24px', backgroundColor: '#f1f5f9' } },
          },
          {
            id: `adm-form-${bundleId}`,
            type: 'ConversionFormBlock',
            category: 'erp_binding',
            props: {
              title: 'Register for Entrance Diagnostic Test',
              formID: 'admission-enquiry-v1',
            },
            styles: { base: { padding: '56px 24px', backgroundColor: '#ffffff' } },
          },
          {
            id: `adm-status-${bundleId}`,
            type: 'AdmissionsStatusTrackerBlock',
            category: 'erp_binding',
            props: {
              title: 'Track Your Application & Test Hall Ticket',
            },
            styles: { base: { padding: '48px 24px', backgroundColor: '#f8fafc' } },
          },
        ]),
        siteName
      ),
  };
}

// -------------------------------------------------------------
// The 10 Fancy-Named Bundles
// -------------------------------------------------------------
export const SITE_BUNDLES: WebsiteBundle[] = [
  {
    id: 'sovereign-scholar',
    fancyName: 'Sovereign Scholar',
    tagline: 'Imperial Prestige for Premier IIT-JEE & Olympiad Coaching',
    targetAudience: 'National entrance coaching institutes & elite academies',
    palette: {
      primary: '#1e3a8a',
      secondary: '#d97706',
      background: '#f8fafc',
      surface: '#ffffff',
      text: '#0f172a',
    },
    navbarVariant: 'centered',
    footerVariant: 'multicolumn',
    pages: [
      createStandardLandingPage(
        'sovereign-scholar',
        'Sovereign Scholar',
        '#1e3a8a',
        'Empower Your Legacy at Sovereign Scholar Academy',
        'Cultivating All-India Top 50 Rankers in JEE Advanced, NEET, and International Olympiads.',
        'Flagship JEE Advanced & Olympiad Batches'
      ),
      createStandardCoursesPage('sovereign-scholar', 'Sovereign Scholar', '#1e3a8a', 'IIT-JEE & KVPY Programs'),
      createStandardFacultyPage('sovereign-scholar', 'Sovereign Scholar', '#1e3a8a'),
      createStandardResultsPage('sovereign-scholar', 'Sovereign Scholar', '#1e3a8a'),
      createStandardAdmissionsPage('sovereign-scholar', 'Sovereign Scholar', '#1e3a8a'),
    ],
  },
  {
    id: 'aetherius-med',
    fancyName: 'Aetherius Med',
    tagline: 'Clinical Precision & Empathy for NEET UG Aspirants',
    targetAudience: 'Pre-Medical coaching & NEET repeaters institutes',
    palette: {
      primary: '#059669',
      secondary: '#0d9488',
      background: '#f0fdf4',
      surface: '#ffffff',
      text: '#064e3b',
    },
    navbarVariant: 'classic',
    footerVariant: 'newsletter',
    pages: [
      createStandardLandingPage(
        'aetherius-med',
        'Aetherius Med',
        '#059669',
        'The Proven Pathway to AIIMS & Top Government Medical Colleges',
        'Rigorous NCERT line-by-line mastery, high-yield biology diagrams, and clinical-level doubt clearing.',
        'NEET UG 1-Year & 2-Year Intensive Programs'
      ),
      createStandardCoursesPage('aetherius-med', 'Aetherius Med', '#059669', 'NEET UG Master Curriculum'),
      createStandardFacultyPage('aetherius-med', 'Aetherius Med', '#059669'),
      createStandardResultsPage('aetherius-med', 'Aetherius Med', '#059669'),
      createStandardAdmissionsPage('aetherius-med', 'Aetherius Med', '#059669'),
    ],
  },
  {
    id: 'quantum-pulse',
    fancyName: 'Quantum Pulse',
    tagline: 'High-Tech Hybrid Learning with Instant CBT Analytics',
    targetAudience: 'Modern EdTech coaching chains & hybrid institutes',
    palette: {
      primary: '#4f46e5',
      secondary: '#7c3aed',
      background: '#faf5ff',
      surface: '#ffffff',
      text: '#1e1b4b',
    },
    navbarVariant: 'glass',
    footerVariant: 'dark',
    pages: [
      createStandardLandingPage(
        'quantum-pulse',
        'Quantum Pulse',
        '#4f46e5',
        'Engineered for Peak Cognitive Performance & Rank Optimization',
        'Real-time CBT simulated test engines, AI-driven weakness diagnosis, and live interactive lectures.',
        'Hybrid Classroom & Live CBT Batches'
      ),
      createStandardCoursesPage('quantum-pulse', 'Quantum Pulse', '#4f46e5', 'Quantum CBT Tracks'),
      createStandardFacultyPage('quantum-pulse', 'Quantum Pulse', '#4f46e5'),
      createStandardResultsPage('quantum-pulse', 'Quantum Pulse', '#4f46e5'),
      createStandardAdmissionsPage('quantum-pulse', 'Quantum Pulse', '#4f46e5'),
    ],
  },
  {
    id: 'vanguardia-civil',
    fancyName: 'Vanguardia Civil',
    tagline: 'Editorial Rigor for UPSC Civil Services & State PSC',
    targetAudience: 'Civil services IAS/IPS preparation institutes',
    palette: {
      primary: '#991b1b',
      secondary: '#b45309',
      background: '#fefce8',
      surface: '#ffffff',
      text: '#451a03',
    },
    navbarVariant: 'split',
    footerVariant: 'multicolumn',
    pages: [
      createStandardLandingPage(
        'vanguardia-civil',
        'Vanguardia Civil',
        '#991b1b',
        'Shaping the Nation’s Future Bureaucrats & Policy Leaders',
        'Comprehensive GS Prelims cum Mains syllabus coverage, daily answer writing analysis, and interview boards.',
        'UPSC Foundation & Optional Mains Batches'
      ),
      createStandardCoursesPage('vanguardia-civil', 'Vanguardia Civil', '#991b1b', 'General Studies & Optional Tracks'),
      createStandardFacultyPage('vanguardia-civil', 'Vanguardia Civil', '#991b1b'),
      createStandardResultsPage('vanguardia-civil', 'Vanguardia Civil', '#991b1b'),
      createStandardAdmissionsPage('vanguardia-civil', 'Vanguardia Civil', '#991b1b'),
    ],
  },
  {
    id: 'solstice-foundation',
    fancyName: 'Solstice Foundation',
    tagline: 'Warm, Nurturing Excellence for Junior Science & NTSE',
    targetAudience: 'Classes 6 to 10 foundation & school curriculum centers',
    palette: {
      primary: '#ea580c',
      secondary: '#0284c7',
      background: '#fff7ed',
      surface: '#ffffff',
      text: '#7c2d12',
    },
    navbarVariant: 'classic',
    footerVariant: 'centered',
    pages: [
      createStandardLandingPage(
        'solstice-foundation',
        'Solstice Foundation',
        '#ea580c',
        'Ignite Curiosity & Scientific Rigor from Class 6 to 10',
        'Early conceptual foundation for NTSE, Junior Science Olympiad, and school board 95%+ achievements.',
        'Junior Champions Foundation Batches'
      ),
      createStandardCoursesPage('solstice-foundation', 'Solstice Foundation', '#ea580c', 'Olympiad & NTSE Curriculum'),
      createStandardFacultyPage('solstice-foundation', 'Solstice Foundation', '#ea580c'),
      createStandardResultsPage('solstice-foundation', 'Solstice Foundation', '#ea580c'),
      createStandardAdmissionsPage('solstice-foundation', 'Solstice Foundation', '#ea580c'),
    ],
  },
  {
    id: 'lumina-nocturne',
    fancyName: 'Lumina Nocturne',
    tagline: 'Obsidian Sleek for GATE, CS & Software Engineering Bootcamps',
    targetAudience: 'GATE test prep, engineering academies & coding bootcamps',
    palette: {
      primary: '#06b6d4',
      secondary: '#3b82f6',
      background: '#0f172a',
      surface: '#1e293b',
      text: '#f8fafc',
    },
    navbarVariant: 'dark',
    footerVariant: 'dark',
    pages: [
      createStandardLandingPage(
        'lumina-nocturne',
        'Lumina Nocturne',
        '#06b6d4',
        'Accelerate Into Top Tier PSUs & IIT M.Tech Programs',
        'Data structures, computer architecture, systems design, and past 20-year GATE rank mastery.',
        'GATE CS & Tech Master Batches'
      ),
      createStandardCoursesPage('lumina-nocturne', 'Lumina Nocturne', '#06b6d4', 'GATE Engineering Tracks'),
      createStandardFacultyPage('lumina-nocturne', 'Lumina Nocturne', '#06b6d4'),
      createStandardResultsPage('lumina-nocturne', 'Lumina Nocturne', '#06b6d4'),
      createStandardAdmissionsPage('lumina-nocturne', 'Lumina Nocturne', '#06b6d4'),
    ],
  },
  {
    id: 'athenaeum-guild',
    fancyName: 'Athenaeum Guild',
    tagline: 'Bespoke 1-on-1 Academic Tutoring & Premium Mentorship',
    targetAudience: 'Private tuition networks & personalized coaching studios',
    palette: {
      primary: '#4d7c0f',
      secondary: '#84cc16',
      background: '#f7fee7',
      surface: '#ffffff',
      text: '#1a2e05',
    },
    navbarVariant: 'minimal',
    footerVariant: 'compact',
    pages: [
      createStandardLandingPage(
        'athenaeum-guild',
        'Athenaeum Guild',
        '#4d7c0f',
        'Handcrafted 1-on-1 Tutoring Designed Around Your Child',
        'Personalized lesson pacing, dedicated doubt solvers, and customized diagnostic assessments.',
        'Private Guild Mentorship Plans'
      ),
      createStandardCoursesPage('athenaeum-guild', 'Athenaeum Guild', '#4d7c0f', '1-on-1 Private Tuition Plans'),
      createStandardFacultyPage('athenaeum-guild', 'Athenaeum Guild', '#4d7c0f'),
      createStandardResultsPage('athenaeum-guild', 'Athenaeum Guild', '#4d7c0f'),
      createStandardAdmissionsPage('athenaeum-guild', 'Athenaeum Guild', '#4d7c0f'),
    ],
  },
  {
    id: 'velocity-rankers',
    fancyName: 'Velocity Rankers',
    tagline: 'High-Impact 90-Day Booster & Dropper Fast-Track Academy',
    targetAudience: 'Crash course centers, test series & dropper batches',
    palette: {
      primary: '#1d4ed8',
      secondary: '#ea580c',
      background: '#eff6ff',
      surface: '#ffffff',
      text: '#172554',
    },
    navbarVariant: 'split',
    footerVariant: 'newsletter',
    pages: [
      createStandardLandingPage(
        'velocity-rankers',
        'Velocity Rankers',
        '#1d4ed8',
        'Double Your Test Scores in 90 Days of High-Speed Drills',
        'Precision problem-solving tricks, speed accuracy frameworks, and intense full-syllabus test marathons.',
        '90-Day Score Booster & Crash Batches'
      ),
      createStandardCoursesPage('velocity-rankers', 'Velocity Rankers', '#1d4ed8', 'Rank Acceleration Programs'),
      createStandardFacultyPage('velocity-rankers', 'Velocity Rankers', '#1d4ed8'),
      createStandardResultsPage('velocity-rankers', 'Velocity Rankers', '#1d4ed8'),
      createStandardAdmissionsPage('velocity-rankers', 'Velocity Rankers', '#1d4ed8'),
    ],
  },
  {
    id: 'monolith-commerce',
    fancyName: 'Monolith Commerce',
    tagline: 'Professional Precision for CA Foundation, Inter & Final',
    targetAudience: 'Chartered Accountancy, CFA & Commerce academies',
    palette: {
      primary: '#18181b',
      secondary: '#059669',
      background: '#fafafa',
      surface: '#ffffff',
      text: '#09090b',
    },
    navbarVariant: 'centered',
    footerVariant: 'multicolumn',
    pages: [
      createStandardLandingPage(
        'monolith-commerce',
        'Monolith Commerce',
        '#18181b',
        'The Gold Standard for Chartered Accountancy & Finance Certifications',
        'Taught exclusively by practicing All-India Ranker Chartered Accountants with real article experience.',
        'CA Foundation, Inter & Final Batches'
      ),
      createStandardCoursesPage('monolith-commerce', 'Monolith Commerce', '#18181b', 'CA & Finance Programs'),
      createStandardFacultyPage('monolith-commerce', 'Monolith Commerce', '#18181b'),
      createStandardResultsPage('monolith-commerce', 'Monolith Commerce', '#18181b'),
      createStandardAdmissionsPage('monolith-commerce', 'Monolith Commerce', '#18181b'),
    ],
  },
  {
    id: 'zenith-global',
    fancyName: 'Zenith Global',
    tagline: 'Ivy League Admissions, SAT 1550+ & Global Test Prep',
    targetAudience: 'Study abroad consultancies, SAT/ACT/IELTS academies',
    palette: {
      primary: '#2563eb',
      secondary: '#e11d48',
      background: '#eff6ff',
      surface: '#ffffff',
      text: '#1e3a8a',
    },
    navbarVariant: 'glass',
    footerVariant: 'centered',
    pages: [
      createStandardLandingPage(
        'zenith-global',
        'Zenith Global',
        '#2563eb',
        'Unlock Ivy League & Top 50 Global University Admissions',
        'Comprehensive SAT Digital coaching, IELTS/TOEFL 8.5+ bands, and bespoke extracurricular profile building.',
        'Digital SAT & Global Scholar Tracks'
      ),
      createStandardCoursesPage('zenith-global', 'Zenith Global', '#2563eb', 'SAT, IELTS & AP Programs'),
      createStandardFacultyPage('zenith-global', 'Zenith Global', '#2563eb'),
      createStandardResultsPage('zenith-global', 'Zenith Global', '#2563eb'),
      createStandardAdmissionsPage('zenith-global', 'Zenith Global', '#2563eb'),
    ],
  },
];

/**
 * Flattens all interchangeable page templates from all 10 bundles
 */
export const ALL_INTERCHANGEABLE_PAGE_TEMPLATES: BundlePageTemplate[] = SITE_BUNDLES.flatMap(
  (bundle) => bundle.pages
);

/**
 * Seamlessly applies an interchangeable page template's body blocks while preserving
 * the site's existing customized SiteNavbar and SiteFooter.
 */
export function applyPageTemplateToSiteChrome(
  templateRoot: BlockNode,
  existingRoot: BlockNode
): BlockNode {
  // Extract navbar and footer from existing root
  const existingChildren = existingRoot.children || [];
  const existingNavbar = existingChildren.find((c) => c.type === 'SiteNavbar');
  const existingFooter = existingChildren.find((c) => c.type === 'SiteFooter');

  // Extract non-chrome body blocks from template root
  const templateBodyBlocks = (templateRoot.children || []).filter(
    (c) => c.type !== 'SiteNavbar' && c.type !== 'SiteFooter'
  );

  const mergedChildren: BlockNode[] = [];
  if (existingNavbar) {
    mergedChildren.push(existingNavbar);
  }
  mergedChildren.push(...templateBodyBlocks);
  if (existingFooter) {
    mergedChildren.push(existingFooter);
  }

  return ensureSiteChromeNodes({
    ...existingRoot,
    children: mergedChildren,
  });
}
